import React, { useEffect, useRef } from 'react';

import { useForm } from 'react-hook-form';

import { useConfigurationStore } from 'store/configurationsStore';
import { IContract, IProject } from 'store/projectContract.store';

import FiltersDropdown from 'components/common/GlobalFiltersInPage/components/FiltersDropdown/FiltersDropdown';
import { GlobalFilterFieldKey } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import {
    GlobalFiltersState,
    IComponentKey,
    IHashmap,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { Widget } from 'components/Dashboards/Reporting/reporting.types';
import { Button } from 'components/DesignSystem/Button';
import classes from 'components/pages/ConfirmWidgetPage/components/ReportGlobalFilters/GlobalFiltersInReportWrapper/GlobalFiltersReportInWrapper.module.scss';
import {
    MORE_FILTERS_BUTTON_KEYS,
    useGetDropdownOptions,
} from 'components/pages/ConfirmWidgetPage/components/ReportGlobalFilters/hooks/useGetDropdownOptions';
import { useGetReportFilterValue } from 'components/pages/ConfirmWidgetPage/components/ReportGlobalFilters/hooks/useGetReportFilterValue';
import { useProgressMeasurement } from 'components/widgets/SubProgramPreferences/components/progressMeasurement/components/progressMeasurementPreference/useProgressMeasurementPreference';

export const COMPONENT_KEYS_FOR_PROGRESS_MEASUREMENT: IComponentKey[] = [
    'topOverdueOverrunActivities',
    'criticalityWeightedSCurve',
    'highLevelProgramLatest',
    'progressSummary',
];

interface Props {
    children: React.ReactNode;
    globalFilters: GlobalFiltersState;
    setGlobalFilters: React.Dispatch<React.SetStateAction<GlobalFiltersState>>;
    selectedProject: IProject | null;
    selectedContract: IContract | null;
    widget?: Widget;
    componentKey: GlobalFilterFieldKey;
}

export const GlobalFiltersInReportWrapper = ({
    children,
    globalFilters,
    setGlobalFilters,
    selectedProject,
    selectedContract,
    widget,
    componentKey,
}: Props) => {
    const { dropdownFiltersOptions, setDropdownFiltersOptions } = useGetDropdownOptions({
        componentKey,
    });
    const displayFiltersDropdown = MORE_FILTERS_BUTTON_KEYS.includes(componentKey);

    const configuration = useConfigurationStore();
    const filtersMapValues = useGetReportFilterValue({ defaultValues: globalFilters });
    const form = useForm({ defaultValues: filtersMapValues });

    const onFiltersSubmit = () => {
        setGlobalFilters(form.watch());
    };

    // Build clean defaults from widget configuration (ignoring saved params)
    const buildCleanDefaults = (): GlobalFiltersState => {
        const componentObj = configuration[componentKey as keyof IHashmap];
        if (!componentObj?.filterConfig) {
            return globalFilters;
        }

        return componentObj.filterConfig.reduce((acc, { key, defaultValues }) => {
            acc[key] = defaultValues;
            return acc;
        }, {} as GlobalFiltersState);
    };

    const handleClearAll = () => {
        const cleanDefaults = buildCleanDefaults();
        form.reset(cleanDefaults);
        onFiltersSubmit();
    };

    // Use refs to track previous values of project and contract
    const prevProjectIdRef = useRef<number | undefined>();
    const prevContractIdRef = useRef<number | undefined>();

    function hasProjectOrContractChanged(
        prevProjectId: number | undefined,
        prevContractId: number | undefined,
        currentProjectId: number | undefined,
        currentContractId: number | undefined,
    ): boolean {
        const projectChanged = prevProjectId !== undefined && prevProjectId !== currentProjectId;
        const contractChanged = prevContractId !== undefined && prevContractId !== currentContractId;

        return projectChanged || contractChanged;
    }

    // Clear all values on project contract change
    useEffect(() => {
        if (selectedProject && selectedContract) {
            if (
                hasProjectOrContractChanged(
                    prevProjectIdRef.current,
                    prevContractIdRef.current,
                    selectedProject.id,
                    selectedContract.id,
                )
            ) {
                handleClearAll();
            }

            // Update refs
            prevProjectIdRef.current = selectedProject.id;
            prevContractIdRef.current = selectedContract.id;
        }
    }, [selectedProject?.id, selectedContract?.id]);

    // Only widgets listed in COMPONENT_KEYS_FOR_PROGRESS_MEASUREMENT use `progressMeasurement` global filter.
    // When true, pull the default ProgressMeasurement value.
    const shouldUseProgressMeasurement =
        componentKey !== undefined && COMPONENT_KEYS_FOR_PROGRESS_MEASUREMENT.includes(componentKey as IComponentKey);
    const { value: defaultProgressMeasurement } = useProgressMeasurement(shouldUseProgressMeasurement);

    // Add `progressMeasurement` to widget global filters.
    useEffect(() => {
        if (defaultProgressMeasurement && selectedProject?.id && selectedContract?.id) {
            const editedWidgetProgressMeasurementValue = widget?.params?.progressMeasurement;

            const isProjectOrContractChanged = hasProjectOrContractChanged(
                prevProjectIdRef.current,
                prevContractIdRef.current,
                selectedProject.id,
                selectedContract.id,
            );

            // if project/contract changed set progressMeasurement with default value
            // else use saved progressMeasurement from params
            const value = isProjectOrContractChanged
                ? defaultProgressMeasurement
                : (editedWidgetProgressMeasurementValue ?? defaultProgressMeasurement);
            form.setValue('progressMeasurement', value);
            onFiltersSubmit();
        }
    }, [defaultProgressMeasurement, selectedProject?.id, selectedContract?.id, widget?.params?.progressMeasurement]);

    return (
        <div className={classes.container}>
            <div className={classes.form}>
                <form onSubmit={form.handleSubmit(onFiltersSubmit)} className={classes.globalFilterComponentContainer}>
                    {React.Children.map(children, (child) => {
                        const fieldName = React.isValidElement(child) ? child.props.name : undefined;
                        const filterIsSelectByDefault = dropdownFiltersOptions[fieldName]?.isSelected === undefined;
                        const dropdownOptionBeenSelected = dropdownFiltersOptions[fieldName]?.isSelected;
                        const isSelected =
                            filterIsSelectByDefault || dropdownOptionBeenSelected || !displayFiltersDropdown;

                        return React.isValidElement(child) && child.props.name
                            ? React.createElement(child.type, {
                                  ...{
                                      ...child.props,
                                      methods: form,
                                      key: child.props.name,
                                      handleFilters: onFiltersSubmit,
                                      value: form.watch(child.props.name),
                                      isSelected,
                                  },
                              })
                            : child;
                    })}
                </form>
            </div>
            <div data-testid={'actions'} className={classes.actions}>
                {displayFiltersDropdown && (
                    <FiltersDropdown
                        selectedFiltersOptions={dropdownFiltersOptions}
                        setSelectedFiltersOptions={setDropdownFiltersOptions}
                        methods={form}
                        defaultValues={globalFilters}
                        handleFilters={() => {
                            setGlobalFilters(form.watch());
                        }}
                    />
                )}
                <div>
                    <Button
                        startIcon={
                            <MuiIcon
                                className={classes.deleteIcon}
                                icon={'delete'}
                                fontSize={'2rem'}
                                type={'outlined'}
                            />
                        }
                        variant={'secondary'}
                        onClick={handleClearAll}
                        className={classes.clearButton}
                    >
                        <span className={classes.clearFiltersText}>Clear Filters</span>
                    </Button>
                </div>
            </div>
        </div>
    );
};
